import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/ui/Button";
import Select from "components/ui/Select";
import Icon from "components/AppIcon";
import useAuth from "hooks/useAuth";
import { useLocationContext } from "context/LocationContext";
import { QUARTER_OPTIONS, YEAR_OPTIONS } from "components/context-navigation/monitoringConstants";
import { getSetupPath } from "config/setupModules";
import { toast } from "sonner";
import {
  OHLGS_ASSESSMENT_STORAGE_KEY,
  buildSectionsFromCatalog,
  createInitialResponsesFromSections,
  getAssessmentRecord,
  getLocationCode,
  getStoredOhlgsCatalog,
  readStoredJson,
  upsertAssessmentRecord,
  writeStoredJson,
} from "./ohlgsStorage";

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function formatDateInputValue(date) {
  return `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
}

function formatTimeInputValue(date) {
  return `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
}

function parseTimestampValue(value) {
  if (!value) {
    return null;
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function createTimestampedMetadata({ buildDefaultName, locationName, assessorName, metadata = {}, createdAt, updatedAt }) {
  const createdAtDate = parseTimestampValue(createdAt) || parseTimestampValue(updatedAt) || new Date();
  const updatedAtDate = parseTimestampValue(updatedAt);

  return {
    subjectName: buildDefaultName(locationName),
    leadAssessor: metadata.leadAssessor || assessorName || "",
    assessmentDate: formatDateInputValue(createdAtDate),
    startTime: formatTimeInputValue(createdAtDate),
    endTime: updatedAtDate ? formatTimeInputValue(updatedAtDate) : "",
  };
}

function getKpiStatus(indicator, response) {
  const hasComment = Boolean(String(response?.comment || "").trim());
  const hasScore = indicator.scored === false
    ? true
    : response?.score !== "" && response?.score !== null && response?.score !== undefined;

  if (!hasComment && !hasScore) return "Not Started";
  if (hasComment && hasScore) return "Complete";
  return "In Progress";
}

function statusClass(status) {
  if (status === "Complete") return "bg-success/10 text-success";
  if (status === "In Progress") return "bg-[#16325a]/10 text-[#16325a]";
  return "bg-muted text-muted-foreground";
}

function getSectionSummary(section, responses) {
  return (Array.isArray(section?.indicators) ? section.indicators : []).reduce(
    (summary, indicator) => {
      const response = responses[indicator.id] || {};
      const numericScore = Number(response.score);
      const hasScore = response.score !== "" && !Number.isNaN(numericScore);
      const isComplete = getKpiStatus(indicator, response) === "Complete";

      if (indicator.scored !== false) {
        summary.totalItems += 1;
        summary.maxScore += 4;

        if (hasScore) {
          summary.score += numericScore;
        }

        if (isComplete) {
          summary.completedItems += 1;
        }
      }

      return summary;
    },
    { totalItems: 0, completedItems: 0, score: 0, maxScore: 0 },
  );
}

function getOverallSummary(sections, responses) {
  return (Array.isArray(sections) ? sections : []).reduce(
    (summary, section) => {
      const sectionSummary = getSectionSummary(section, responses);

      summary.totalItems += sectionSummary.totalItems;
      summary.completedItems += sectionSummary.completedItems;
      summary.score += sectionSummary.score;
      summary.maxScore += sectionSummary.maxScore;

      return summary;
    },
    { totalItems: 0, completedItems: 0, score: 0, maxScore: 0 },
  );
}

function LocationRequiredState({ level }) {
  const label = level === "region" ? "Region" : "District";

  return (
    <div className="main-content">
      <div className="card">
        <h2 className="mb-2 text-xl font-semibold text-foreground">{label} Selection Required</h2>
        <p className="text-muted-foreground">
          Select a {label.toLowerCase()} from the sidebar before opening the OHLGS report module.
        </p>
      </div>
    </div>
  );
}

function NoSetupState({ onOpenSetup }) {
  return (
    <div className="main-content">
      <div className="card space-y-4">
        <div>
          <h2 className="mb-2 text-xl font-semibold text-foreground">No OHLGS Setup Found</h2>
          <p className="text-muted-foreground">
            The shared OHLGS setup catalog has not been configured yet. Complete the setup first, then return here
            to fill the assessment template.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Button type="button" onClick={onOpenSetup}>
            <Icon name="Settings" className="h-4 w-4" />
            Open OHLGS Setup
          </Button>
        </div>
      </div>
    </div>
  );
}

function KpiAssessmentItem({
  indicator,
  response,
  onScoreChange,
  onCommentChange,
}) {
  const [isExpanded, setIsExpanded] = useState(true);
  const status = getKpiStatus(indicator, response);

  return (
    <div className="ml-4 mt-4 first:mt-0">
      <div className="overflow-hidden rounded-xl border border-border bg-background">
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="flex w-full items-center justify-between gap-3 p-4 text-left transition-colors hover:bg-muted/10"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Icon name={isExpanded ? "ChevronDown" : "ChevronRight"} className="h-4 w-4 text-muted-foreground" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-foreground">
                  {indicator.code}
                </span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass(status)}`}>
                  {status}
                </span>
                {indicator.scored === false ? (
                  <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                    Not Scored
                  </span>
                ) : null}
              </div>
              <p className="mt-1 truncate text-sm font-medium text-foreground">
                {indicator.statement[0] || "Untitled KPI"}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-xs text-muted-foreground">Rate</p>
            <p className="text-sm font-semibold text-foreground">
              {indicator.scored === false
                ? "N/A"
                : response?.score !== "" && response?.score !== null && response?.score !== undefined
                  ? response.score
                  : "--"}
            </p>
          </div>
        </button>

        {isExpanded ? (
          <div className="space-y-4 border-t border-border p-4">
            <div className="space-y-3">
              {indicator.statement.map((line) => (
                <p key={line} className="text-sm font-medium leading-6 text-foreground">
                  {line}
                </p>
              ))}
            </div>

            {indicator.scoringGuide.length > 0 ? (
              <div className="rounded-lg bg-muted/15 p-3">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Scoring Guide
                </p>
                <ul className="space-y-1.5">
                  {indicator.scoringGuide.map((line) => (
                    <li key={line} className="flex items-start gap-2 text-sm text-foreground">
                      <span className="mt-2 h-1 w-1 rounded-full bg-[#16325a]" />
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Rate (0-4)
                </span>
                <select
                  value={response?.score ?? ""}
                  onChange={(event) => onScoreChange(indicator.id, event.target.value)}
                  disabled={indicator.scored === false}
                  className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                >
                  <option value="">Select</option>
                  {[0, 1, 2, 3, 4].map((score) => (
                    <option key={score} value={score}>
                      {score}
                    </option>
                  ))}
                </select>
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Justification / Comments / Observation
                </span>
                <textarea
                  rows={6}
                  value={response?.comment || ""}
                  onChange={(event) => onCommentChange(indicator.id, event.target.value)}
                  placeholder="Capture the justification, comments, observation, and evidence for this KPI..."
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function KpaAssessmentCard({
  section,
  responses,
  index,
  totalSections,
  onScoreChange,
  onCommentChange,
  onSave,
  saving,
}) {
  const sectionSummary = getSectionSummary(section, responses);

  return (
    <section className="card overflow-hidden">
      <div className="border-b border-border p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-foreground">
                {section.code}
              </span>
              <span className="text-xs text-muted-foreground">
                KPA {index + 1}/{totalSections}
              </span>
            </div>
            <h3 className="mt-2 text-lg font-semibold text-foreground">{section.title}</h3>
            {section.notes?.length > 0 ? (
              <div className="mt-3 space-y-2">
                {section.notes.map((note) => (
                  <p key={note} className="text-sm text-muted-foreground">
                    {note}
                  </p>
                ))}
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Score</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {sectionSummary.score}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {sectionSummary.maxScore}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Completed</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {sectionSummary.completedItems}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {sectionSummary.totalItems}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">KPIs</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {section.indicators.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        {section.indicators.map((indicator) => (
          <KpiAssessmentItem
            key={indicator.id}
            indicator={indicator}
            response={responses[indicator.id] || { score: "", comment: "" }}
            onScoreChange={onScoreChange}
            onCommentChange={onCommentChange}
          />
        ))}
      </div>

      <div className="flex justify-end border-t border-border bg-muted/5 p-4">
        <Button type="button" onClick={onSave} disabled={saving}>
          {saving ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
          {saving ? "Saving..." : "Save KPA"}
        </Button>
      </div>
    </section>
  );
}

export default function OhlgsAssessmentFlow({
  workflowLabel,
  level,
  badgeLabel,
  pageTitle,
  locationSummaryLabel,
  locationRequiredLabel,
  buildDefaultName,
  generalCommentsPlaceholder,
  recommendationsPlaceholder,
}) {
  const navigate = useNavigate();
  const { currentLocation } = useLocationContext();
  const { user } = useAuth();
  const locationCode = getLocationCode(currentLocation);
  const assessorName = user?.user?.fullName || user?.user?.username || "";
  const defaultYear =
    YEAR_OPTIONS.find((option) => option.value === String(new Date().getFullYear())) ||
    YEAR_OPTIONS[YEAR_OPTIONS.length - 1];

  const [selectedYear, setSelectedYear] = useState(defaultYear);
  const [selectedQuarter, setSelectedQuarter] = useState(QUARTER_OPTIONS[0]);
  const [hasSelectedPeriod, setHasSelectedPeriod] = useState(false);
  const [catalog, setCatalog] = useState(() => getStoredOhlgsCatalog());
  const [assessmentRows, setAssessmentRows] = useState(() =>
    readStoredJson(OHLGS_ASSESSMENT_STORAGE_KEY, []),
  );
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showAllItems, setShowAllItems] = useState(false);
  const [metadata, setMetadata] = useState(() =>
    createTimestampedMetadata({
      buildDefaultName,
      locationName: currentLocation?.name,
      assessorName,
    }),
  );
  const [responses, setResponses] = useState({});
  const [notes, setNotes] = useState({
    generalComments: "",
    recommendations: "",
  });
  const [saveScope, setSaveScope] = useState(null);

  const setupPath = getSetupPath("ohlgs-setup");
  const sectionsFromSetup = useMemo(() => buildSectionsFromCatalog(catalog), [catalog]);
  const activeAssessment = useMemo(
    () =>
      getAssessmentRecord(assessmentRows, {
        level,
        locationCode,
        year: selectedYear?.value,
        quarter: selectedQuarter?.value,
      }) || null,
    [assessmentRows, level, locationCode, selectedQuarter?.value, selectedYear?.value],
  );
  const templateSections = useMemo(
    () => activeAssessment?.templateSections || sectionsFromSetup,
    [activeAssessment?.templateSections, sectionsFromSetup],
  );

  useEffect(() => {
    if (!hasSelectedPeriod) return;

    if (activeAssessment) {
      setMetadata(
        createTimestampedMetadata({
          buildDefaultName,
          locationName: currentLocation?.name,
          assessorName,
          metadata: activeAssessment.metadata,
          createdAt: activeAssessment.createdAt,
          updatedAt: activeAssessment.updatedAt,
        }),
      );
      setResponses(
        activeAssessment.responses ||
          createInitialResponsesFromSections(activeAssessment.templateSections || templateSections),
      );
      setNotes(
        activeAssessment.notes || {
          generalComments: "",
          recommendations: "",
        },
      );
      setCurrentStepIndex(0);
      setShowAllItems(false);
      return;
    }

    setMetadata(
      createTimestampedMetadata({
        buildDefaultName,
        locationName: currentLocation?.name,
        assessorName,
      }),
    );
    setResponses(createInitialResponsesFromSections(templateSections));
    setNotes({
      generalComments: "",
      recommendations: "",
    });
    setCurrentStepIndex(0);
    setShowAllItems(false);
  }, [
    activeAssessment,
    assessorName,
    buildDefaultName,
    currentLocation?.name,
    hasSelectedPeriod,
    templateSections,
  ]);

  const indicatorProgress = useMemo(
    () =>
      templateSections.map((section) => ({
        ...section,
        summary: getSectionSummary(section, responses),
      })),
    [responses, templateSections],
  );

  const overallSummary = useMemo(
    () => getOverallSummary(templateSections, responses),
    [responses, templateSections],
  );

  const currentSection = indicatorProgress[currentStepIndex] || null;
  const showSummarySection = showAllItems || currentStepIndex === indicatorProgress.length - 1;

  if (currentLocation?.level !== level) {
    return <LocationRequiredState level={level} />;
  }

  const handleLoadAssessment = () => {
    const latestCatalog = getStoredOhlgsCatalog();
    setCatalog(latestCatalog);
    setAssessmentRows(readStoredJson(OHLGS_ASSESSMENT_STORAGE_KEY, []));
    setHasSelectedPeriod(true);
  };

  const updateMetadata = (field) => (event) => {
    setMetadata((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const updateScore = (indicatorId, value) => {
    setResponses((current) => ({
      ...current,
      [indicatorId]: {
        ...current[indicatorId],
        score: value === "" ? "" : Number(value),
      },
    }));
  };

  const updateComment = (indicatorId, value) => {
    setResponses((current) => ({
      ...current,
      [indicatorId]: {
        ...current[indicatorId],
        comment: value,
      },
    }));
  };

  const updateNotes = (field) => (event) => {
    setNotes((current) => ({
      ...current,
      [field]: event.target.value,
    }));
  };

  const persistAssessment = (scope) => {
    if (!selectedYear || !selectedQuarter) {
      toast.error("Select the year and quarter before saving.");
      return;
    }

    if (templateSections.length === 0) {
      toast.error("No OHLGS setup catalog is available for this assessment.");
      return;
    }

    setSaveScope(scope);
    const savedAt = new Date().toISOString();
    const createdAt = activeAssessment?.createdAt || savedAt;
    const timestampedMetadata = createTimestampedMetadata({
      buildDefaultName,
      locationName: currentLocation?.name,
      assessorName,
      metadata,
      createdAt,
      updatedAt: savedAt,
    });

    const nextRecord = {
      assessmentId:
        activeAssessment?.assessmentId ||
        `${level}:${locationCode}:${selectedYear.value}:${selectedQuarter.value}`,
      level,
      locationCode,
      locationName: currentLocation?.name || "",
      year: selectedYear.value,
      quarter: selectedQuarter.value,
      metadata: timestampedMetadata,
      responses,
      notes,
      templateSections,
      createdAt,
      updatedAt: savedAt,
      updatedBy: user?.user?.username || assessorName || "system",
    };

    const nextRows = upsertAssessmentRecord(assessmentRows, nextRecord);
    setMetadata(timestampedMetadata);
    setAssessmentRows(nextRows);
    writeStoredJson(OHLGS_ASSESSMENT_STORAGE_KEY, nextRows);
    setSaveScope(null);
    toast.success(scope === "summary" ? "OHLGS notes saved" : "OHLGS KPA saved");
  };

  if (!hasSelectedPeriod) {
    return (
      <div className="main-content">
        <div className="card p-8">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-border bg-muted">
              <Icon name="Calendar" className="h-6 w-6 text-muted-foreground" />
            </div>
            <h2 className="mb-1.5 text-lg font-medium text-foreground">Select assessment period</h2>
            <p className="mx-auto max-w-sm text-sm leading-relaxed text-muted-foreground">
              Choose the year and quarter to open the OHLGS assessment form for {currentLocation?.name}.
            </p>
          </div>

          <div className="mx-auto max-w-sm space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Year
                </label>
                <Select
                  options={YEAR_OPTIONS}
                  value={selectedYear}
                  onChange={(option) => {
                    setSelectedYear(option);
                    setHasSelectedPeriod(false);
                  }}
                  placeholder="Select year"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Quarter
                </label>
                <Select
                  options={QUARTER_OPTIONS}
                  value={selectedQuarter}
                  onChange={(option) => {
                    setSelectedQuarter(option);
                    setHasSelectedPeriod(false);
                  }}
                  placeholder="Select quarter"
                />
              </div>
            </div>

            <Button
              type="button"
              className="w-full"
              onClick={handleLoadAssessment}
              disabled={!selectedYear || !selectedQuarter}
            >
              <Icon name="Search" className="mr-2 h-4 w-4" />
              Load OHLGS Form
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (templateSections.length === 0) {
    return <NoSetupState onOpenSetup={() => navigate(setupPath)} />;
  }

  return (
    <div className="min-h-full bg-background">
      <div className="main-content space-y-5 pb-6">
        <section className="card p-4 md:p-5">
          <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground">{workflowLabel}</p>
                <h2 className="mt-1 text-lg font-semibold text-foreground">{pageTitle}</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {currentLocation?.name} {selectedYear?.value} {selectedQuarter?.value}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setShowAllItems((current) => !current)}>
                  <Icon name="List" className="h-4 w-4" />
                  {showAllItems ? "Back to Step View" : "Show All KPAs"}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => navigate(setupPath)}>
                  <Icon name="Settings" className="h-4 w-4" />
                  Open Setup
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setHasSelectedPeriod(false)}>
                  <Icon name="RefreshCw" className="h-4 w-4" />
                  Change Period
                </Button>
              </div>
            </div>

            <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-end">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="MapPin" className="h-3.5 w-3.5" /> {currentLocation?.name || locationRequiredLabel}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="Hash" className="h-3.5 w-3.5" /> {locationCode || "-"}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-[#16325a]/10 px-2.5 py-1 text-[#16325a]">
                  <Icon name="Calendar" className="h-3.5 w-3.5" /> {selectedYear?.value} {selectedQuarter?.value}
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-muted-foreground">
                  <Icon name="CheckSquare" className="h-3.5 w-3.5" /> {overallSummary.completedItems}/{overallSummary.totalItems} complete
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="card p-4 md:p-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Lead Assessor
              </span>
              <input
                value={metadata.leadAssessor}
                onChange={updateMetadata("leadAssessor")}
                className="w-full rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                placeholder="Enter lead assessor"
              />
            </label>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Date</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.assessmentDate || "-"}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Start Time</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.startTime || "-"}</p>
            </div>
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">End Time</p>
              <p className="mt-1 text-sm font-semibold text-foreground">{metadata.endTime || "Pending save"}</p>
            </div>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Overall Score</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {overallSummary.score}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {overallSummary.maxScore}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">Scored KPIs</p>
              <p className="mt-1 text-lg font-semibold text-foreground">
                {overallSummary.completedItems}
                <span className="ml-1 text-sm font-medium text-muted-foreground">/ {overallSummary.totalItems}</span>
              </p>
            </div>
            <div className="rounded-xl border border-border bg-background px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{locationSummaryLabel}</p>
              <p className="mt-1 text-sm font-semibold text-foreground">
                {currentLocation?.name || locationRequiredLabel}
              </p>
            </div>
          </div>
        </section>

        <div className="space-y-5">
          {showAllItems ? (
            indicatorProgress.map((section, index) => (
              <KpaAssessmentCard
                key={section.id}
                section={section}
                responses={responses}
                index={index}
                totalSections={indicatorProgress.length}
                onScoreChange={updateScore}
                onCommentChange={updateComment}
                onSave={() => persistAssessment(section.id)}
                saving={saveScope === section.id}
              />
            ))
          ) : currentSection ? (
            <KpaAssessmentCard
              section={currentSection}
              responses={responses}
              index={currentStepIndex}
              totalSections={indicatorProgress.length}
              onScoreChange={updateScore}
              onCommentChange={updateComment}
              onSave={() => persistAssessment(currentSection.id)}
              saving={saveScope === currentSection.id}
            />
          ) : null}

          {!showAllItems && indicatorProgress.length > 0 ? (
            <section className="card p-4 md:p-5">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCurrentStepIndex((current) => Math.max(current - 1, 0))}
                  disabled={currentStepIndex === 0}
                >
                  <Icon name="ChevronLeft" className="h-4 w-4" />
                  Previous
                </Button>
                <div className="text-center">
                  <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Assessment Navigation</p>
                  <p className="mt-1 text-sm text-foreground">
                    KPA {currentStepIndex + 1} of {indicatorProgress.length}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={() =>
                    setCurrentStepIndex((current) => Math.min(current + 1, indicatorProgress.length - 1))
                  }
                  disabled={currentStepIndex === indicatorProgress.length - 1}
                >
                  Next
                  <Icon name="ChevronRight" className="h-4 w-4" />
                </Button>
              </div>
            </section>
          ) : null}
        </div>

        {showSummarySection ? (
          <section className="card overflow-hidden p-0">
            <div className="border-b border-border px-5 py-4">
              <div className="flex flex-col gap-2">
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Assessment Notes
                </p>
                <h4 className="text-lg font-semibold text-foreground">General Comments and Recommendations</h4>
              </div>
            </div>

            <div className="grid gap-6 p-5 lg:grid-cols-2">
              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  General Comments / Observations
                </span>
                <textarea
                  rows={10}
                  value={notes.generalComments}
                  onChange={updateNotes("generalComments")}
                  placeholder={generalCommentsPlaceholder}
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>

              <label className="space-y-2">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                  Recommendations
                </span>
                <textarea
                  rows={10}
                  value={notes.recommendations}
                  onChange={updateNotes("recommendations")}
                  placeholder={recommendationsPlaceholder}
                  className="w-full rounded-2xl border border-input bg-background px-3 py-3 text-sm leading-6 text-foreground outline-none transition focus:border-[#16325a] focus:ring-2 focus:ring-[#16325a]/20"
                />
              </label>
            </div>

            <div className="flex justify-end border-t border-border px-5 py-4">
              <Button type="button" onClick={() => persistAssessment("summary")} disabled={saveScope === "summary"}>
                {saveScope === "summary" ? <Icon name="Loader2" className="h-4 w-4 animate-spin" /> : <Icon name="Save" className="h-4 w-4" />}
                {saveScope === "summary" ? "Saving..." : "Save Assessment Notes"}
              </Button>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}
